<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import { useI18n } from 'vue-i18n'
import apiInstance from '@/utils/api'

use([CanvasRenderer, LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent])

const { t, locale } = useI18n()

interface DailyStatRow {
  date: string
  total_orders: number
  total_revenue: number
  failed_transaction: number
  failed_transaction_ratio: number
  average_check: number
}

type ChartMetric = 'total_revenue' | 'total_orders' | 'average_check' | 'failed_transaction'

const props = defineProps<{
  visible: boolean
  initialDateRange: [string, string]
  metric: ChartMetric
  restaurantId?: number | string | null
  color?: string
}>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
}>()

// Internal date range — initialized from dashboard, editable inside modal
const localDateRange = ref<[string, string]>([...props.initialDateRange])

// Sync when parent range changes (e.g. user picks new range on dashboard while modal is closed)
watch(() => props.initialDateRange, (val) => {
  localDateRange.value = [...val]
})

// Reset date to dashboard range on open/close — localDateRange watcher handles fetch
watch(() => props.visible, (val) => {
  if (val) {
    localDateRange.value = [...props.initialDateRange]
  } else {
    localDateRange.value = [...props.initialDateRange]
    dailyData.value = []
  }
})
watch(localDateRange, () => {
  if (props.visible) fetchDailyStats()
}, { deep: true })

// ---- Date picker shortcuts (mirrors dashboard) ----
const getFormattedDate = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const shortcuts = computed(() => {
  const today = new Date()
  return [
    {
      text: t('dashboard.last_7_days'),
      value: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6)
        return [start, today]
      },
    },
    {
      text: t('dashboard.last_week'),
      value: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 7)
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 1)
        return [start, end]
      },
    },
    {
      text: t('dashboard.current_week'),
      value: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay())
        return [start, today]
      },
    },
    {
      text: t('dashboard.last_30_days'),
      value: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)
        return [start, today]
      },
    },
    {
      text: t('dashboard.last_month'),
      value: () => {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const end = new Date(today.getFullYear(), today.getMonth(), 0)
        return [start, end]
      },
    },
    {
      text: t('dashboard.current_month'),
      value: () => {
        const start = new Date(today.getFullYear(), today.getMonth(), 1)
        return [start, today]
      },
    },
  ]
})

// ---- Data fetching ----
const loading = ref(false)
const dailyData = ref<DailyStatRow[]>([])

const fetchDailyStats = async () => {
  if (!localDateRange.value || localDateRange.value.length !== 2) return
  loading.value = true
  dailyData.value = []
  try {
    const response = props.restaurantId
      ? await apiInstance.get(`/restaurant/${props.restaurantId}/restaurant-daily-stats`, {
          params: {
            start_date: localDateRange.value[0],
            end_date: localDateRange.value[1],
          },
        })
      : await apiInstance.post('/user/dashboard-daily-stats', {
          start_date: localDateRange.value[0],
          end_date: localDateRange.value[1],
        })
    if (!response.data.error) {
      dailyData.value = response.data.data
    }
  } catch (error) {
    console.error('Error fetching daily stats:', error)
  } finally {
    loading.value = false
  }
}

// ---- Chart ----
const formatDay = (dateStr: string) => {
  const [, month, day] = dateStr.split('-')
  return `${day}.${month}`
}

const metricConfig: Record<ChartMetric, { title: string; color: string; yAxisLabel: string; isCurrency: boolean }> = {
  total_revenue:     { title: '', color: '#16a34a', yAxisLabel: '€', isCurrency: true },
  total_orders:      { title: '', color: '#2563eb', yAxisLabel: '#', isCurrency: false },
  average_check:     { title: '', color: '#9333ea', yAxisLabel: '€', isCurrency: true },
  failed_transaction:{ title: '', color: '#dc2626', yAxisLabel: '# / %', isCurrency: false },
}

const chartTitle = computed(() => {
  const map: Record<ChartMetric, string> = {
    total_revenue: t('dashboard.stats.totalRevenue'),
    total_orders: t('dashboard.stats.totalOrders'),
    average_check: t('dashboard.stats.avgCheck'),
    failed_transaction: t('dashboard.stats.failedTransactions'),
  }
  return map[props.metric]
})

const chartOption = computed(() => {
  const m = props.metric
  const cfg = { ...metricConfig[m], color: props.color || metricConfig[m].color }
  const dates = dailyData.value.map(d => formatDay(d.date))

  const tooltipFormatter = (params: any[]) => {
    let html = `<div style="font-weight:600;margin-bottom:6px">${params[0]?.axisValue}</div>`
    for (const p of params) {
      const val = p.seriesName === t('dashboard.stats.failedTransactionsRatio')
        ? `${p.value}%`
        : cfg.isCurrency
          ? `€${Number(p.value).toFixed(2)}`
          : String(p.value)
      html += `<div style="display:flex;align-items:center;gap:8px;margin:2px 0">
        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color}"></span>
        <span style="flex:1">${p.seriesName}</span>
        <span style="font-weight:600;margin-left:12px">${val}</span>
      </div>`
    }
    return html
  }

  const baseAxis = {
    type: 'value' as const,
    splitLine: { lineStyle: { type: 'dashed' as const, color: '#e5e7eb' } },
  }

  // For failed_transaction: dual axis (count left, ratio right)
  const isFailed = m === 'failed_transaction'

  const yAxis = isFailed
    ? [
        { ...baseAxis, axisLabel: { fontSize: 11, formatter: (v: number) => String(v) } },
        { ...baseAxis, axisLabel: { fontSize: 11, formatter: (v: number) => `${v}%` }, splitLine: { show: false } },
      ]
    : [
        {
          ...baseAxis,
          axisLabel: {
            formatter: cfg.isCurrency ? (v: number) => `€${v}` : (v: number) => String(v),
            fontSize: 11,
          },
        },
      ]

  const series = isFailed
    ? [
        {
          name: t('dashboard.stats.failedTransactions'),
          type: 'line' as const,
          yAxisIndex: 0,
          data: dailyData.value.map(d => d.failed_transaction),
          smooth: true, symbol: 'circle', symbolSize: 6,
          lineStyle: { width: 2, color: '#dc2626' },
          itemStyle: { color: '#dc2626' },
          areaStyle: { color: 'rgba(220,38,38,0.08)' },
        },
        {
          name: t('dashboard.stats.failedTransactionsRatio'),
          type: 'line' as const,
          yAxisIndex: 1,
          data: dailyData.value.map(d => d.failed_transaction_ratio),
          smooth: true, symbol: 'diamond', symbolSize: 6,
          lineStyle: { width: 2, color: '#f97316', type: 'dashed' as const },
          itemStyle: { color: '#f97316' },
        },
      ]
    : [
        {
          name: chartTitle.value,
          type: 'line' as const,
          yAxisIndex: 0,
          data: dailyData.value.map(d => d[m as keyof typeof d] as number),
          smooth: true, symbol: 'circle', symbolSize: 6,
          lineStyle: { width: 2, color: cfg.color },
          itemStyle: { color: cfg.color },
          areaStyle: { color: `${cfg.color}14` },
        },
      ]

  const legendData = isFailed
    ? [t('dashboard.stats.failedTransactions'), t('dashboard.stats.failedTransactionsRatio')]
    : [chartTitle.value]

  return {
    backgroundColor: '#fff',
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: tooltipFormatter },
    legend: { bottom: 24, data: legendData, textStyle: { fontSize: 12 } },
    grid: { left: 16, right: 16, top: 16, bottom: 64, containLabel: true },
    xAxis: { type: 'category' as const, data: dates, axisLabel: { fontSize: 11 }, boundaryGap: false },
    yAxis,
    dataZoom: [{ type: 'inside', start: 0, end: 100 }],
    series,
  }
})
</script>

<template>
  <el-dialog
    :model-value="visible"
    @update:model-value="emit('update:visible', $event)"
    width="min(95vw, 1200px)"
    :style="{ borderRadius: '12px', maxHeight: '90vh' }"
    align-center
    :title="`${chartTitle} — ${t('dashboard.chartModal.title')}`"
    draggable
  >
    <!-- Toolbar: date picker right -->
    <div class="flex items-center justify-end mb-3">
      <div class="w-fit">
        <el-date-picker
          size="small"
          v-model="localDateRange"
          type="daterange"
          :range-separator="t('dashboard.date_range_separator')"
          :start-placeholder="t('dashboard.start_date')"
          :end-placeholder="t('dashboard.end_date')"
          format="DD.MM.YYYY"
          value-format="YYYY-MM-DD"
          :disabled-date="(date) => date > new Date()"
          :shortcuts="shortcuts"
          :clearable="false"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center chart-area">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="dailyData.length === 0" class="flex items-center justify-center text-gray-400 chart-area">
      {{ t('dashboard.chartModal.noData') }}
    </div>

    <!-- Chart -->
    <div v-else class="chart-area">
      <v-chart
        :option="chartOption"
        style="width: 100%; height: 100%"
        autoresize
      />
    </div>
  </el-dialog>
</template>

<style scoped>
.chart-area {
  height: min(480px, calc(100vh - 260px));
}
</style>
